import { useQuery } from "@tanstack/react-query";

export interface UnreadChannel {
  channelId: string;
  channelType: string;
  projectId: string | null;
  projectName: string;
  unreadCount: number;
  firstUnreadMessageId: string | null;
  lastAuthorId: number | null;
  lastAuthorName: string | null;
  lastAuthorColor: string | null;
  isMuted?: boolean;
}

const FIXED_COLORS = {
  global: "#3B82F6",
  topic: "#F59E0B", // amber-500 to match topic channel styling
  client: "#F97316", // orange-500 — client (Kunden) chat notifications
} as const;

export function useUnreadCounts() {
  return useQuery<UnreadChannel[]>({
    queryKey: ["/api/chat/unread-counts-all"],
    staleTime: 30000,
    refetchInterval: 60000,
  });
}

export function useProjectHud() {
  const { data: unreadChannels = [], isLoading } = useUnreadCounts();

  const projectChannels = unreadChannels
    .filter((c) => (c.channelType === "project" || c.channelType === "client") && c.projectId)
    .map((c) => ({
      ...c,
      displayText: c.projectId!,
      // Client-chat notifications are always orange so they're instantly
      // distinguishable from internal project chat bubbles.
      backgroundColor: c.channelType === "client"
        ? FIXED_COLORS.client
        : (c.lastAuthorColor || "#6B7280"),
    }));

  return { channels: projectChannels, isLoading };
}

export function useDockHud() {
  const { data: unreadChannels = [], isLoading } = useUnreadCounts();

  const dockChannels = unreadChannels
    .filter((c) => ["global", "topic", "direct"].includes(c.channelType))
    .filter((c) => !c.isMuted)
    .map((c) => {
      let backgroundColor: string;
      let displayText: string;

      if (c.channelType === "global") {
        backgroundColor = FIXED_COLORS.global;
        displayText = "Global";
      } else if (c.channelType === "topic") {
        backgroundColor = FIXED_COLORS.topic;
        displayText = c.projectName || "Topic";
      } else {
        backgroundColor = c.lastAuthorColor || "#6B7280";
        displayText = c.lastAuthorName || c.projectName || "DM";
      }

      if (displayText.length > 15) {
        displayText = displayText.substring(0, 12) + "...";
      }

      return {
        ...c,
        displayText,
        backgroundColor,
      };
    });

  return { channels: dockChannels, isLoading };
}
